import { Middleware } from "@reduxjs/toolkit";
import { RootState } from "@/store";
import { logger } from "@/utils/logger";
import {
  MESSAGE_CONFIG,
  ERROR_CONFIG,
  MONITORING_CONFIG,
} from "@/config/chat8-stabilization";
import { Store, Dispatch, Action } from "redux";

interface StabilizationMetrics {
  messageCount: number;
  errorCount: number;
  lastError: string | null;
  lastMessageTime: number;
  connectionUptime: number;
  reconnectCount: number;
}

class Chat8StabilizationMiddleware {
  private metrics: StabilizationMetrics = {
    messageCount: 0,
    errorCount: 0,
    lastError: null,
    lastMessageTime: Date.now(),
    connectionUptime: 0,
    reconnectCount: 0,
  };

  private healthCheckInterval: NodeJS.Timeout | null = null;
  private lastHealthCheck = Date.now();

  constructor() {
    this.startHealthCheck();
  }

  private startHealthCheck() {
    if (this.healthCheckInterval) {
      clearInterval(this.healthCheckInterval);
    }

    this.healthCheckInterval = setInterval(() => {
      this.performHealthCheck();
    }, MONITORING_CONFIG.HEALTH_CHECK_INTERVAL);
  }

  private performHealthCheck() {
    const now = Date.now();

    // Verificar se há muitas mensagens em um curto período
    if (this.metrics.messageCount > MESSAGE_CONFIG.MESSAGE_RATE_LIMIT) {
      logger.warn(
        "[Chat8Stabilization] High message rate detected:",
        this.metrics.messageCount,
      );
      this.metrics.messageCount = 0; // Reset counter
    }

    // Verificar se há muitos erros
    if (this.metrics.errorCount > ERROR_CONFIG.MAX_RETRY_ATTEMPTS) {
      logger.error(
        "[Chat8Stabilization] High error rate detected:",
        this.metrics.errorCount,
      );
      this.metrics.errorCount = 0; // Reset counter
    }

    // Verificar tempo desde última mensagem apenas se houver mensagens
    const timeSinceLastMessage = now - this.metrics.lastMessageTime;
    if (timeSinceLastMessage > MESSAGE_CONFIG.MESSAGE_TIMEOUT && this.metrics.messageCount > 0) {
      logger.warn("[Chat8Stabilization] No recent messages detected");
    }

    this.lastHealthCheck = now;
  }

  private updateMetrics(action: any) {
    // Atualizar métricas baseado na ação
    if (typeof action?.type === "string" && action.type.includes("message")) {
      this.metrics.messageCount++;
      this.metrics.lastMessageTime = Date.now();
    }

    if (typeof action?.type === "string" && action.type.includes("error")) {
      this.metrics.errorCount++;
      this.metrics.lastError = action.payload?.message || action.type;
    }

    if (typeof action?.type === "string" && action.type.includes("reconnect")) {
      this.metrics.reconnectCount++;
    }

    if (typeof action?.type === "string" && action.type.includes("connected")) {
      this.metrics.connectionUptime = Date.now();
    }
  }

  private validateState(state: RootState) {
    const errors: string[] = [];

    // Validar estado do chat
    if (state.chat) {
      const { messages, currentSession } = state.chat;

      // Verificar se há muitas mensagens
      if (
        messages &&
        messages.length > MESSAGE_CONFIG.MAX_MESSAGES_PER_SESSION
      ) {
        errors.push(`Too many messages in session: ${messages.length}`);
      }

      // Verificar se a sessão está válida
      if (currentSession && !currentSession.id) {
        errors.push("Invalid session state: missing session ID");
      }
    }

    // Validar estado do WebSocket
    if (state.websocket) {
      const { reconnectAttempts, isConnected, isReconnecting } =
        state.websocket;

      // Verificar tentativas de reconexão
      if (reconnectAttempts > ERROR_CONFIG.MAX_RETRY_ATTEMPTS) {
        errors.push(`Too many reconnection attempts: ${reconnectAttempts}`);
      }

      // Verificar estado inconsistente
      if (isConnected && isReconnecting) {
        errors.push("Inconsistent WebSocket state: connected and reconnecting");
      }
    }

    // Validar estado dos documentos
    if (state.documents) {
      const { items, loading, error } = state.documents;

      // Verificar se há erro persistente
      if (error && typeof error === "string" && error.length > 0) {
        errors.push(`Document error: ${error}`);
      }

      // Verificar se há muitos documentos carregados
      if (items && items.length > 100) {
        errors.push(`Too many documents loaded: ${items.length}`);
      }
    }

    return errors;
  }

  private handleStabilizationErrors(errors: string[]) {
    if (errors.length > 0) {
      logger.error("[Chat8Stabilization] State validation errors:", errors);

      // Implementar ações corretivas baseadas nos erros
      errors.forEach((error) => {
        if (error.includes("Too many messages")) {
          // Limpar mensagens antigas
          logger.info("[Chat8Stabilization] Clearing old messages");
        }

        if (error.includes("Too many reconnection attempts")) {
          // Resetar contador de reconexão
          logger.info("[Chat8Stabilization] Resetting reconnection attempts");
        }

        if (error.includes("Invalid session state")) {
          // Limpar sessão inválida
          logger.info("[Chat8Stabilization] Clearing invalid session");
        }
      });
    }
  }

  public getMiddleware(): Middleware<{}, RootState> {
    return (store: Store) => (next: Dispatch) => (action: Action) => {
      const prevState = store.getState();

      // Atualizar métricas
      this.updateMetrics(action);

      // Executar ação
      const result = next(action);

      const nextState = store.getState();

      // Validar estado após ação
      const validationErrors = this.validateState(nextState);
      this.handleStabilizationErrors(validationErrors);

      // Log de debug se necessário
      // if (MONITORING_CONFIG.LOG_LEVEL === "debug") {
      //   logger.debug("[Chat8Stabilization] Action processed:", {
      //     type:
      //       typeof action === "object" && action !== null && "type" in action
      //         ? action.type
      //         : "unknown",
      //     messageCount: this.metrics.messageCount,
      //     errorCount: this.metrics.errorCount,
      //     reconnectCount: this.metrics.reconnectCount,
      //   });
      // }

      return result;
    };
  }

  public getMetrics(): StabilizationMetrics {
    return { ...this.metrics };
  }

  public resetMetrics(): void {
    this.metrics = {
      messageCount: 0,
      errorCount: 0,
      lastError: null,
      lastMessageTime: Date.now(),
      connectionUptime: 0,
      reconnectCount: 0,
    };
  }

  public cleanup(): void {
    if (this.healthCheckInterval) {
      clearInterval(this.healthCheckInterval);
      this.healthCheckInterval = null;
    }
  }
}

// Instância singleton do middleware
const chat8StabilizationMiddleware = new Chat8StabilizationMiddleware();

// Middleware para uso no Redux
export const chat8StabilizationMiddlewareFn =
  chat8StabilizationMiddleware.getMiddleware();

// Funções utilitárias
export const getStabilizationMetrics = () =>
  chat8StabilizationMiddleware.getMetrics();
export const resetStabilizationMetrics = () =>
  chat8StabilizationMiddleware.resetMetrics();
export const cleanupStabilizationMiddleware = () =>
  chat8StabilizationMiddleware.cleanup();

export default chat8StabilizationMiddlewareFn;
